import React, {useEffect, useRef, useState} from 'react';
import {Box, Button, LinearProgress, Paper, Typography} from '@mui/material';
import {useSearchParams} from 'react-router-dom';
import {FirebaseError} from 'firebase/app';
import {PhoneAuthProvider, RecaptchaVerifier} from 'firebase/auth';
import {firebaseAuth} from '../../shared/services/firebase.service';
import {displayPhone, toE164} from '../../shared/phone';

/** Where the page hands its result to the iPhone app (its web authentication sheet catches this scheme). */
export const APP_CALLBACK = 'savaquan://phone-verify';

export const appCallbackURL = (result: {sessionInfo: string} | {error: string}) => {
  const [name, value] = 'sessionInfo' in result ? ['sessionInfo', result.sessionInfo] : ['error', result.error];
  return `${APP_CALLBACK}?${name}=${encodeURIComponent(value)}`;
};

const RECAPTCHA_ID = 'phone-verify-recaptcha';

// Phone sign-in for the iPhone app, which talks to Firebase over REST with no
// SDK. Firebase texts a code only to an app it has verified, and without APNs
// that is a reCAPTCHA on a web page: this page. The app opens it in a web
// authentication sheet with ?phone=<E.164>; it runs reCAPTCHA, has Firebase
// text the code, and hands the verification id back to the app as
// savaquan://phone-verify?sessionInfo=…, or ?error=<Firebase code> when
// Firebase refused. The app then signs in with the code it is given.
const PhoneVerifyPage = () => {
  const [params] = useSearchParams();
  const phone = toE164(params.get('phone'));
  const [result, setResult] = useState<string | null>(null);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) {
      return;
    }
    started.current = true;
    const finish = (url: string) => {
      setResult(url);
      window.location.replace(url);
    };
    if (!phone) {
      if (params.has('phone')) {
        finish(appCallbackURL({error: 'auth/invalid-phone-number'}));
      }
      return;
    }
    const verifier = new RecaptchaVerifier(firebaseAuth, RECAPTCHA_ID, {size: 'invisible'});
    new PhoneAuthProvider(firebaseAuth).verifyPhoneNumber(phone, verifier)
      .then(sessionInfo => finish(appCallbackURL({sessionInfo})))
      .catch(e => finish(appCallbackURL({error: e instanceof FirebaseError ? e.code : 'auth/internal-error'})))
      .finally(() => verifier.clear());
  }, [phone, params]);

  return (
    <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={2}>
      <Paper elevation={3} sx={{p: 4, width: '100%', maxWidth: 400}}>
        <Typography variant="h5" align="center" gutterBottom>
          SavaQuan
        </Typography>
        {!phone && !result ? (
          <Typography color="text.secondary" align="center">
            This page sends a sign-in code for the SavaQuan iPhone app. Open the app and choose Sign in with phone.
          </Typography>
        ) : result ? (
          <>
            <Typography color="text.secondary" align="center" sx={{mb: 2}}>
              Return to the SavaQuan app to go on.
            </Typography>
            <Button variant="contained" fullWidth href={result}>Back to the app</Button>
          </>
        ) : (
          <>
            <Typography align="center" sx={{mb: 2}}>
              Sending a code to {displayPhone(phone!)}…
            </Typography>
            <LinearProgress/>
            <Typography variant="body2" color="text.secondary" align="center" sx={{mt: 2}}>
              First we check that you're not a robot. If a puzzle appears, solve it to go on.
            </Typography>
          </>
        )}
        <Box id={RECAPTCHA_ID}/>
      </Paper>
    </Box>
  );
};

export default PhoneVerifyPage;
